import type { NewStaffingRequest, ScheduleChange, ScheduleRow, ScheduleStaffing, ScheduleSync, StaffingRequest } from "@shyne/shared/api";
import { uuid } from "@/interfaces";
import { activeOrganizationId } from "@/lib/active-organization";
import { api } from "@/lib/api";
import { requirePermission } from "@/lib/permissions";
import { DailyWorkScheduleSyncResult, MonthlyScheduleRule, schedule_assignment_kind } from "@/models/daily-work-schedule.model";
import { createLocalId } from "@/lib/daily-work/master-schedule";

export interface DailyWorkScheduleAssignmentJoin {
  id: uuid;
  assignment_kind: schedule_assignment_kind;
  assigned_user: uuid | null;
  assigned_org: uuid | null;
  quantity: number;
  rate_override: number | null;
  assigned_user_data: { id: uuid; name: string } | null;
  assigned_org_data: { id: uuid; display_name: string } | null;
}

export interface DailyWorkScheduleRowJoin {
  id: uuid;
  daily_work: uuid;
  weekday: number;
  service: uuid;
  weekly_tasks: string[];
  monthly_rules: MonthlyScheduleRule[];
  max_hours: number | null;
  duration_label: string | null;
  instructions: string | null;
  is_active: boolean;
  position: number;
  created_at: string;
  daily_work_data: {
    id: uuid;
    status: string;
    start_date: string | null;
    end_date: string | null;
    property: { id: uuid; name: string } | null;
  };
  service_data: { id: uuid; name: string } | null;
  assignments: DailyWorkScheduleAssignmentJoin[];
}

export interface SaveScheduleAssignmentInput {
  id?: uuid;
  assignment_kind: schedule_assignment_kind;
  assigned_user?: uuid | null;
  assigned_org?: uuid | null;
  quantity?: number;
  rate_override?: number | null;
}

export interface SaveScheduleRowInput {
  id?: uuid;
  daily_work: uuid;
  weekdays: number[];
  service: uuid;
  weekly_tasks: string[];
  monthly_rules: MonthlyScheduleRule[];
  max_hours?: number | null;
  duration_label?: string | null;
  instructions?: string | null;
  assignments: SaveScheduleAssignmentInput[];
}

export type ScheduleRowSave = { rows: DailyWorkScheduleRowJoin[]; sync: DailyWorkScheduleSyncResult };

const toScheduleSyncResult = (sync: ScheduleSync): DailyWorkScheduleSyncResult => ({
  inserted: sync.added,
  updated: sync.updated,
  deleted: sync.removed,
  kept_locked: sync.keptLocked,
  kept_detached: sync.keptDetached,
  notified: sync.notified,
  window_start: sync.window?.start ?? null,
  window_end: sync.window?.end ?? null,
  frozen: sync.frozen,
});

const named = (record: { id?: uuid; name?: string } | null) => (record ? { id: record.id, name: record.name } : null);

const toAssignment = (entry: ScheduleStaffing): DailyWorkScheduleAssignmentJoin => ({
  id: entry.id,
  assignment_kind: entry.subcontractor ? "company" : "individual",
  assigned_user: entry.worker?.id ?? null,
  assigned_org: entry.subcontractor?.id ?? null,
  quantity: entry.quantity,
  rate_override: entry.rateOverride,
  assigned_user_data: named(entry.worker),
  assigned_org_data: entry.subcontractor ? { id: entry.subcontractor.id, display_name: entry.subcontractor.name } : null,
});

const toScheduleRow = (row: ScheduleRow): DailyWorkScheduleRowJoin => ({
  id: row.id,
  daily_work: row.contract.id,
  weekday: row.weekday,
  service: row.service.id,
  weekly_tasks: row.weeklyTasks,
  monthly_rules: row.monthlyRules.map((rule) => ({ id: createLocalId("monthly"), occurrence: rule.occurrence, tasks: rule.tasks })),
  max_hours: row.maxHours,
  duration_label: row.durationLabel,
  instructions: row.instructions,
  is_active: row.active,
  position: row.position,
  created_at: row.createdAt,
  daily_work_data: { id: row.contract.id, status: row.contract.status, start_date: row.contract.start, end_date: row.contract.end, property: named(row.contract.property) },
  service_data: named(row.service),
  assignments: row.staffing.map(toAssignment),
});

function toNewStaffing(input: SaveScheduleAssignmentInput): NewStaffingRequest {
  const rateOverride = input.rate_override ?? null;
  if (input.assignment_kind === "individual") return { worker: input.assigned_user as uuid, rateOverride };
  return { subcontractor: input.assigned_org as uuid, quantity: Math.max(1, Math.trunc(input.quantity ?? 1)), rateOverride };
}

const toStaffing = (input: SaveScheduleAssignmentInput): StaffingRequest => ({ ...(input.id ? { id: input.id } : {}), ...toNewStaffing(input) });

const rowContent = (input: SaveScheduleRowInput) => ({
  service: input.service,
  weeklyTasks: input.weekly_tasks,
  monthlyRules: input.monthly_rules.map((rule) => ({ occurrence: rule.occurrence, tasks: rule.tasks })),
  maxHours: input.max_hours ?? null,
  durationLabel: input.duration_label ?? null,
  instructions: input.instructions ?? null,
  actingOrganization: activeOrganizationId(),
});

const toSave = ({ rows, sync }: ScheduleChange): ScheduleRowSave => ({ rows: rows.map(toScheduleRow), sync: toScheduleSyncResult(sync) });

export const DailyWorkScheduleRepository = {
  async listForVendor(): Promise<DailyWorkScheduleRowJoin[]> {
    await requirePermission("daily_work.view");

    const { items } = await api.scheduleRows.list({ actingOrganization: activeOrganizationId() });
    return items.map(toScheduleRow);
  },

  async saveRow(input: SaveScheduleRowInput): Promise<ScheduleRowSave> {
    await requirePermission("daily_work.update");

    if (input.id) {
      const [weekday] = input.weekdays;
      return toSave(await api.scheduleRows.setDetails(input.id, { ...rowContent(input), weekday, staffing: input.assignments.map(toStaffing) }));
    }
    return toSave(await api.contracts.addScheduleRows(input.daily_work, { ...rowContent(input), weekdays: input.weekdays, staffing: input.assignments.map(toNewStaffing) }));
  },

  async archiveRow(id: uuid): Promise<DailyWorkScheduleSyncResult> {
    await requirePermission("daily_work.update");

    return toScheduleSyncResult(await api.scheduleRows.archive(id, { actingOrganization: activeOrganizationId() }));
  },

  async syncSchedule(dailyWorkId: uuid): Promise<DailyWorkScheduleSyncResult> {
    await requirePermission("daily_work.update");

    return toScheduleSyncResult(await api.contracts.syncSchedule(dailyWorkId, { actingOrganization: activeOrganizationId() }));
  },

  async copyScheduleToContract(sourceDailyWorkId: uuid, targetDailyWorkId: uuid): Promise<number> {
    await requirePermission("daily_work.update");

    const { rows } = await api.contracts.copySchedule(targetDailyWorkId, { from: sourceDailyWorkId, actingOrganization: activeOrganizationId() });
    return rows.length;
  },
};
